import { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QRCodePositioner } from './QRCodePositioner';

interface WallpaperQRCodeProps {
  savedQRCode: string | null;
  hasQRCode: boolean;
  uploadWallpaperBlob: (blob: Blob) => Promise<void>;
}

export function WallpaperQRCode({
  savedQRCode,
  hasQRCode,
  uploadWallpaperBlob,
}: WallpaperQRCodeProps) {
  // État local pour stocker les données de l'image
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [position, setPosition] = useState<number>(() => {
    const savedPosition = localStorage.getItem('wallpaperQRPosition');
    return savedPosition ? parseInt(savedPosition) : 4;
  });
  // Le curseur va de 0 à 100
  const [qrSizePercent, setQrSizePercent] = useState<number>(() => {
    const savedSize = localStorage.getItem('wallpaperQRSize');
    return savedSize ? parseInt(savedSize) : 0;
  });
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Persister uniquement la position et la taille dans localStorage
  useEffect(() => {
    localStorage.setItem('wallpaperQRPosition', position.toString());
    localStorage.setItem('wallpaperQRSize', qrSizePercent.toString());
  }, [position, qrSizePercent]);

  // Fonction utilitaire pour redimensionner une image en format portrait
  const resizeImage = (img: HTMLImageElement): string => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Impossible de créer le contexte canvas');

    // Dimensions cibles pour mobile (format portrait 9:16)
    const TARGET_WIDTH = 1080;
    const TARGET_HEIGHT = 1920;

    // Calculer les ratios
    const ratioW = TARGET_WIDTH / img.width;
    const ratioH = TARGET_HEIGHT / img.height;
    const ratio = Math.max(ratioW, ratioH);

    // Calculer les nouvelles dimensions pour couvrir tout le canvas
    const newWidth = img.width * ratio;
    const newHeight = img.height * ratio;

    // Définir les dimensions du canvas au format portrait cible
    canvas.width = TARGET_WIDTH;
    canvas.height = TARGET_HEIGHT;

    // Remplir le fond en noir pour éviter les zones transparentes
    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, TARGET_WIDTH, TARGET_HEIGHT);

    // Centrer l'image
    const x = (TARGET_WIDTH - newWidth) / 2;
    const y = (TARGET_HEIGHT - newHeight) / 2;

    // Dessiner l'image redimensionnée
    ctx.drawImage(img, x, y, newWidth, newHeight);

    // Retourner l'image au format JPEG avec une qualité de 0.8 pour réduire la taille
    return canvas.toDataURL('image/jpeg', 0.8);
  };

  // Fonction pour vérifier la taille d'une chaîne base64
  const checkBase64Size = (base64String: string): boolean => {
    // Estimation de la taille en octets
    const sizeInBytes = Math.ceil((base64String.length * 3) / 4);
    const sizeInMB = sizeInBytes / (1024 * 1024);
    return sizeInMB <= 4; // Limite à 4MB pour être sûr
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Vérifier la taille du fichier (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        alert('L\'image est trop grande. La taille maximale est de 10MB. L\'image sera automatiquement redimensionnée.');
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          try {
            // Redimensionner l'image au format portrait
            const dataUrl = resizeImage(img);
            
            // Vérifier si la taille du base64 est acceptable
            if (!checkBase64Size(dataUrl)) {
              alert('L\'image est toujours trop grande après redimensionnement. Veuillez utiliser une image plus petite ou de qualité inférieure.');
              return;
            }

            setBackgroundImage(dataUrl);
            setQrSizePercent(0);
          } catch (error) {
            console.error('Erreur lors du traitement de l\'image:', error);
            alert('Une erreur est survenue lors du traitement de l\'image. Veuillez réessayer avec une image différente.');
          }
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    }
    event.target.value = '';
  };

  // Calculer la taille d'affichage en pourcentage pour la prévisualisation
  const getPreviewSize = () => {
    // Taille minimale de 20% de la cellule, maximale de 90%
    const minSize = 20;
    const maxSize = 90;
    // Interpolation linéaire entre min et max selon le pourcentage du curseur
    return minSize + ((maxSize - minSize) * qrSizePercent / 100);
  };

  const handleSave = async () => {
    if (!backgroundImage || !savedQRCode) return;
    setIsSaving(true);

    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Créer et charger l'image de fond
      const bgImage = new Image();
      bgImage.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        bgImage.onload = resolve;
        bgImage.onerror = reject;
        bgImage.src = backgroundImage;
      });

      // Créer et charger le QR code
      const qrImage = new Image();
      qrImage.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
        qrImage.onload = resolve;
        qrImage.onerror = reject;
        qrImage.src = savedQRCode;
      });

      // Définir les dimensions du canvas
      canvas.width = bgImage.width;
      canvas.height = bgImage.height;

      // Dessiner l'image de fond
      ctx.drawImage(bgImage, 0, 0);

      // Calculer la position du QR code
      const cols = 3;
      const rows = 3;
      const cellWidth = canvas.width / cols;
      const cellHeight = canvas.height / rows;
      const col = position % cols;
      const row = Math.floor(position / cols);
      
      // Calculer la taille du QR code en fonction du pourcentage
      const qrSize = (getPreviewSize() / 100) * cellWidth;
      const x = cellWidth * col + (cellWidth - qrSize) / 2;
      const y = cellHeight * row + (cellHeight - qrSize) / 2;

      // Créer un conteneur blanc pour le QR code avec un padding proportionnel
      const padding = qrSize * 0.05; // 5% de la taille du QR code
      ctx.fillStyle = 'white';
      ctx.fillRect(x - padding, y - padding, qrSize + padding * 2, qrSize + padding * 2);
      
      // Dessiner le QR code
      ctx.drawImage(qrImage, x, y, qrSize, qrSize);

      // Convertir le canvas en blob et sauvegarder
      const blob = await new Promise<Blob>((resolve) => 
        canvas.toBlob((blob) => resolve(blob!), 'image/png', 1)
      );
      
      const newGeneratedImage = canvas.toDataURL('image/png');
      setGeneratedImage(newGeneratedImage);
      await uploadWallpaperBlob(blob);

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = 'wallpaper-qrcode.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const resetAllStates = () => {
    setBackgroundImage(null);
    setPosition(4);
    setQrSizePercent(0);
    setGeneratedImage(null);
  };

  return (
    <div className="space-y-4">
      {/* Input pour choisir l'image */}
      <div className="flex justify-between items-center">
        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            id="background-image-input"
          />
          <label
            htmlFor="background-image-input"
            className="cursor-pointer inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Choisir une image
          </label>
        </div>
        {backgroundImage && (
          <button
            onClick={resetAllStates}
            className="px-4 py-2 text-red-600 hover:text-red-700 transition-colors"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* Contrôle de la taille du QR Code */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Taille du QR Code ({qrSizePercent}%)
        </label>
        <div className="relative w-full">
          <input
            type="range"
            min="0"
            max="100"
            value={qrSizePercent}
            onChange={(e) => setQrSizePercent(Number(e.target.value))}
            className="absolute w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:relative [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary-600 [&::-webkit-slider-thumb]:z-20
              [&::-moz-range-thumb]:relative [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary-600 [&::-moz-range-thumb]:z-20"
          />
          <div 
            className="absolute top-0 left-0 h-2 bg-primary-600 rounded-l-lg" 
            style={{ 
              width: `${qrSizePercent}%`,
              zIndex: 10
            }}
          />
        </div>
      </div>

      {/* Zone d'affichage de l'image et du QR code */}
      {backgroundImage && (
        <div className="w-full max-w-2xl mx-auto">
          <QRCodePositioner
            backgroundImage={backgroundImage}
            qrCodeElement={
              <div className="aspect-square bg-white rounded-md shadow-lg flex items-center justify-center" style={{
                width: `${getPreviewSize()}%`
              }}>
                <img 
                  src={savedQRCode} 
                  alt="QR Code"
                  className="w-full h-full p-1"
                />
              </div>
            }
            position={position}
            onPositionChange={setPosition}
          />
        </div>
      )}

      {/* Boutons de sauvegarde et téléchargement */}
      {backgroundImage && (
        <div className="flex justify-end space-x-4">
          <button
            onClick={handleSave}
            disabled={!backgroundImage || !hasQRCode || isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300"
          >
            {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
          </button>
          <button
            onClick={handleDownload}
            disabled={!generatedImage}
            className="inline-flex items-center px-4 py-2 border border-primary-600 text-sm font-medium rounded-md text-primary-600 bg-transparent hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Télécharger
          </button>
        </div>
      )}
    </div>
  );
}